import { FolderProtocolsWidgetStore } from './folder-protocols-widget.store';
import { Component, computed, inject, input, viewChild } from '@angular/core';
import { TableComponent } from '../../../../modules/shared/components/kit/table/table.component';
import {
    TableColumn,
    TableColumnOffersLayout,
    TableColumnType,
} from '../../../../modules/shared/components/kit/table/table.types';
import {
    dateField,
    dayField,
    numberField,
    offersField,
    userChipField,
} from '../../../../modules/shared/components/kit/table/table.fields';
import { getProtocolWidgetWorksFrag } from './folder-protocols-widget.query';
import { UserChipRenderTypeEnum } from '../../../kit/user-chip/user-chip.component.types';
import { socialOnlyWorkTypes } from '@libs/types/src/common/enum/work-type.enum';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { Operator } from '../../../../../../../data-grid/src/grid/grid-request.dto';
import { TranslatePipe } from '@ngx-translate/core';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import {
    DEFAULT_TAG_COLOR,
} from '../../setting/service-settings-offers-page/service-settings-offer-detail-page/service-settings-offer-detail-page.component';
import { TagComponent } from '../../../kit/tag/tag.component';
import { FormatOfferDurationPipe } from '../../../../modules/shared/pipes/format-service-offer-duration.pipe';
import { ColorContrastPipe } from '../../../../modules/shared/pipes/color-contrast.pipe';
import { Chip } from '@openng/optimus-ui/chip';
import { NgStyle } from '@angular/common';
import { IconComponent } from '../../../kit/icon/icon.component';
import { DatesFormatDurationPipe } from '../../../../modules/shared/pipes/format-duration.pipe';
import { Divider } from '@openng/optimus-ui/divider';
import { router } from '../../../../modules/shared/shared.global-service';
import { workUri } from '../../../../utils/router.utils';
import { checkOk } from '../../../../modules/content/content.error';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';

@Component({
    selector: 'app-folder-protocols-widget',
    templateUrl: './folder-protocols-widget.component.html',
    styleUrl: './folder-protocols-widget.component.scss',
    providers: [FolderProtocolsWidgetStore],
    imports: [
        TableComponent,
        TranslatePipe,
        PrimeTemplate,
        TagComponent,
        FormatOfferDurationPipe,
        ColorContrastPipe,
        Chip,
        IconComponent,
        NgStyle,
        DatesFormatDurationPipe,
        Divider,
    ],
})
export class FolderProtocolsWidgetComponent {
    protected readonly store = inject(FolderProtocolsWidgetStore);
    protected readonly confirm = inject(ConfirmBuilder);

    public folderId = input.required<number, string | number>({
        transform: (it) => Number(it),
    });

    public readonly protocolsTable = viewChild(TableComponent);

    protected readonly prefix = 'person.dashboard.protocols.';

    protected columns: TableColumn[] = [
        dayField('day', 'dateFrom'),
        { ...dateField('onlyDateFrom'), dataKey: 'dateFrom' },
        {
            name: 'timeFrom',
            dataKey: 'dateFrom',
            type: TableColumnType.Time,
        },
        {
            name: 'timeTo',
            dataKey: 'dateTo',
            type: TableColumnType.Time,
        },
        { ...numberField('applications'), filterable: false, sortable: false },
        offersField('offers', TableColumnOffersLayout.Vertical, {
            filterable: true,
        }),
        {
            name: 'tags',
            filterable: true,
        },
        userChipField('users', UserChipRenderTypeEnum.User, {
            data: (users: any[]) => users?.[0],
            filterable: true,
            sortable: true,
        }),
        {
            name: 'workType',
            filterable: true,
            sortable: true,
        },
    ];

    protected additionalFilter = computed(() => {
        return new FilterBuilder()
            .where('folderIdWithProtocolsSharing', this.folderId().toString(), Operator.EQ)
            .where('workType', socialOnlyWorkTypes, Operator.IN)
            .where('hasFilledProtocol', true)
            .filter().filter;
    });

    constructor() {
        this.store.onSuccess(() => this.protocolsTable()?.reload(), 'createWork', 'updateWork');
    }

    protected async detailWork(item: any): Promise<void> {
        await router.navigateByUrl(workUri({ workType: item.workType, id: item.id }, item.serviceId));
    }

    protected deleteWork(event: any): void {
        this.confirm.confirmDelete(async () => {
            const res = await this.store.api.deleteWork({ id: event.item.id });
            checkOk(res, 'delete') && this.protocolsTable()?.reload();
        }, event);
    }

    protected readonly getProtocolWidgetWorksFrag = getProtocolWidgetWorksFrag;
    protected readonly socialOnlyWorkTypes = socialOnlyWorkTypes;
    protected readonly DEFAULT_TAG_COLOR = DEFAULT_TAG_COLOR;
}
